import React, { useState, useEffect } from 'react';
import api from '../utils/api';

export default function ManageUserAccessModal({ user, companies, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentAccess, setCurrentAccess] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [search, setSearch] = useState('');

  const displayName = user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.name;

  useEffect(() => {
    const loadAccess = async () => {
      try {
        const res = await api.get(`/admin/users/${user.id}/company-access`);
        setCurrentAccess(res.data);
        setSelectedIds(new Set(res.data.map(c => c.id)));
      } catch (err) {
        setError('Failed to load current access');
      } finally {
        setLoading(false);
      }
    };
    loadAccess();
  }, [user.id]);

  const primaryCompanyId = currentAccess.find(c => c.is_primary)?.id;

  const toggle = (companyId) => {
    if (companyId === primaryCompanyId) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const added = companies.filter(c => selectedIds.has(c.id) && !currentAccess.find(ca => ca.id === c.id));
  const removed = currentAccess.filter(c => !c.is_primary && !selectedIds.has(c.id));
  const hasChanges = added.length > 0 || removed.length > 0;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/admin/users/${user.id}/company-access`, {
        company_ids: Array.from(selectedIds),
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update access');
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const filtered = companies.filter(c =>
    c.company_name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400">Loading access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] flex flex-col">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Manage Client Access</h3>
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">{displayName} &middot; <span className="text-gray-400 dark:text-gray-500">{user.email}</span></p>
        </div>

        {confirming ? (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg mb-4">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-3">Please confirm these changes:</h4>

              {added.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Adding access to:</p>
                  {added.map(c => (
                    <div key={c.id} className="flex items-center space-x-2 py-1">
                      <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
                      <span className="text-sm text-gray-800 dark:text-gray-100">{c.company_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {removed.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Removing access from:</p>
                  {removed.map(c => (
                    <div key={c.id} className="flex items-center space-x-2 py-1">
                      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></span>
                      <span className="text-sm text-gray-800 dark:text-gray-100">{c.company_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
                Go Back
              </button>
              <button type="button" onClick={handleSave} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Confirm Changes'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients..."
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm text-sm px-3 py-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 mb-4 max-h-64">
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500 p-3">No clients match your search.</p>
              ) : (
                filtered.map(c => {
                  const isPrimary = c.id === primaryCompanyId;
                  const isChecked = selectedIds.has(c.id);
                  return (
                    <label key={c.id}
                      className={`flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${isPrimary ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isPrimary}
                        onChange={() => toggle(c.id)}
                        className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500 mr-3"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-800 dark:text-gray-100">{c.company_name}</span>
                        {isPrimary && (
                          <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-medium">Primary</span>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            <div className="text-xs text-gray-400 dark:text-gray-500 mb-3">
              {selectedIds.size} client{selectedIds.size !== 1 ? 's' : ''} selected
              {primaryCompanyId && <span> (primary cannot be removed)</span>}
            </div>

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <div className="flex justify-end space-x-3">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="button" onClick={() => setConfirming(true)} disabled={!hasChanges}
                className="px-4 py-2 text-sm text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Review Changes
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
